import type { GenerationType, ScenarioAsset, ScenarioAssetGeneration } from './scenarioTypes';

/** Centralized asset filtering and classification utilities */
export const AssetFilters = {
  /**
   * Check if an asset is a 3D model
   */
  is3D(asset: ScenarioAsset): boolean {
    const metadataType = asset.metadata?.type || '';
    const assetFormat = asset.metadata?.format || '';
    const mimeType = asset.mimeType || '';

    return (
      metadataType === 'txt23d' ||
      metadataType === 'img23d' ||
      assetFormat === 'glb' ||
      mimeType.includes('gltf')
    );
  },

  /**
   * Check if an asset is a skybox
   */
  isSkybox(asset: ScenarioAsset): boolean {
    const source = asset.source || '';
    const metadataType = asset.metadata?.type || '';

    return (
      source === 'skybox-base-360' ||
      source === 'skybox-upscale-360' ||
      metadataType === 'skybox-base-360' ||
      metadataType === 'skybox-upscale-360' ||
      source.includes('skybox') ||
      metadataType.includes('skybox')
    );
  },

  /**
   * Check if an asset is a texture (including texture maps)
   */
  isTexture(asset: ScenarioAsset): boolean {
    const metadataType = asset.metadata?.type || '';
    const source = asset.source || '';

    // Exclude 3D and skybox assets
    if (this.is3D(asset) || this.isSkybox(asset)) {
      return false;
    }

    // Include texture and all texture maps (normal, albedo, roughness, etc.)
    return (
      metadataType.includes('texture') ||
      metadataType === 'inference-txt2img-texture'
    );
  },

  /**
   * Check if an asset is a regular image (not 3D, skybox, or texture)
   */
  isImage(asset: ScenarioAsset): boolean {
    // Exclude 3D, skybox, and texture assets
    return !this.is3D(asset) && !this.isSkybox(asset) && !this.isTexture(asset);
  },

  /**
   * Check if an asset has a specific user tag
   */
  hasUserTag(asset: ScenarioAsset, userTag: string): boolean {
    const assetTags = asset.tags ?? [];
    return assetTags.includes(userTag);
  },

  /**
   * Check if an asset has any user tag
   */
  hasAnyUserTag(asset: ScenarioAsset): boolean {
    const assetTags = asset.tags ?? [];
    return assetTags.some((tag: string) => tag.startsWith('user:'));
  },

  /**
   * Filter assets by generation type
   */
  filterByType(assets: ScenarioAsset[], type?: GenerationType): ScenarioAsset[] {
    if (!type) return assets;

    return assets.filter(asset => {
      switch (type) {
        case '3d':
          return this.is3D(asset);
        case 'skybox':
          return this.isSkybox(asset);
        case 'texture':
          return this.isTexture(asset);
        case 'image':
          return this.isImage(asset);
        default:
          return true;
      }
    });
  },

  /**
   * Filter assets by user tag (includes legacy untagged assets)
   */
  filterByUser(assets: ScenarioAsset[], userTag: string): {
    userAssets: ScenarioAsset[];
    legacyAssets: ScenarioAsset[];
    otherUserAssets: ScenarioAsset[];
  } {
    const userAssets: ScenarioAsset[] = [];
    const legacyAssets: ScenarioAsset[] = [];
    const otherUserAssets: ScenarioAsset[] = [];

    assets.forEach(asset => {
      const hasUserTag = this.hasUserTag(asset, userTag);
      const hasAnyUserTag = this.hasAnyUserTag(asset);

      if (hasUserTag) {
        userAssets.push(asset);
      } else if (!hasAnyUserTag) {
        legacyAssets.push(asset);
      } else {
        otherUserAssets.push(asset);
      }
    });

    return { userAssets, legacyAssets, otherUserAssets };
  },

  /**
   * Group assets by their parent job ID (generation)
   */
  groupByGeneration(assets: ScenarioAsset[]): ScenarioAssetGeneration[] {
    const groupMap = new Map<string, ScenarioAsset[]>();

    // Group by parentJobId
    assets.forEach(asset => {
      const jobId = asset.metadata.parentJobId;
      if (!groupMap.has(jobId)) {
        groupMap.set(jobId, []);
      }
      groupMap.get(jobId)!.push(asset);
    });

    // Convert to generation objects and sort
    const generations: ScenarioAssetGeneration[] = [];

    groupMap.forEach((assets, jobId) => {
      // Sort assets within generation by outputIndex
      assets.sort((a, b) => a.outputIndex - b.outputIndex);

      // Use data from first asset (they should all have same metadata)
      const firstAsset = assets[0];

      generations.push({
        jobId,
        prompt: firstAsset.metadata.prompt,
        seed: firstAsset.metadata.seed,
        numInferenceSteps: firstAsset.metadata.numInferenceSteps,
        geometryEnforcement: firstAsset.metadata.geometryEnforcement,
        style: firstAsset.metadata.style,
        createdAt: firstAsset.createdAt,
        assets,
      });
    });

    // Sort generations by creation date (newest first)
    generations.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    return generations;
  },

  /**
   * Get texture map subtype from metadata
   */
  getTextureMapType(asset: ScenarioAsset): string {
    const metadataType = asset.metadata?.type || '';

    if (metadataType.includes('albedo') || metadataType.includes('Albedo')) {
      return '_albedo';
    }
    if (metadataType.includes('normal') || metadataType.includes('Normal')) {
      return '_normal';
    }
    if (metadataType.includes('height') || metadataType.includes('Height')) {
      return '_height';
    }
    if (metadataType.includes('metallic') || metadataType.includes('Metallic')) {
      return '_metallic';
    }
    if (metadataType.includes('smoothness') || metadataType.includes('Smoothness')) {
      return '_smoothness';
    }
    if (metadataType.includes('roughness') || metadataType.includes('Roughness')) {
      return '_roughness';
    }
    if (metadataType.includes('ao') || metadataType.includes('AO')) {
      return '_ao';
    }
    if (metadataType.includes('edge') || metadataType.includes('Edge')) {
      return '_edge';
    }
    if (metadataType === 'texture') {
      return '_preview';
    }

    return '';
  },

  /**
   * Determine asset category and subtype for file naming
   */
  getAssetCategory(asset: ScenarioAsset): { category: string; subtype: string } {
    const metadataType = asset.metadata?.type || '';
    const source = asset.source || '';

    // Check for 3D assets
    if (this.is3D(asset)) {
      return { category: '3d-model', subtype: '' };
    }

    // Check for skybox
    if (this.isSkybox(asset)) {
      return { category: 'skybox', subtype: '' };
    }

    // Check for texture maps
    if (this.isTexture(asset)) {
      return { category: 'texture', subtype: this.getTextureMapType(asset) };
    }

    // Regular image
    return { category: 'image', subtype: '' };
  },
};

